<template>
  <v-dialog v-model="showDialog" persistent>
    <v-card outlined class="pa-3" v-if="loaded">
      <v-col class="text-right px-4">
        <v-btn icon @click="closeDialog">
          <v-icon>close</v-icon>
        </v-btn>
      </v-col>

      <v-card-title>
        <h6 class="mb-0 font-weight-bold">{{ mergePreview.building.name}}</h6>
      </v-card-title>
      <v-card-text>
        <v-row>
          <!-- Left side -->
          <v-col>
            <LabelField
                :label="$t('common:object-number')"
                :value="mergePreview.building.objectNumber ? mergePreview.building.objectNumber : '-'"
                flex
            />

            <LabelField
                :label="$t('common:created_at')"
                :value="mergePreview.building.createdAt"
                flex
            />

            <LabelField
                :label="$t('common:last-updated-on')"
                :value="mergePreview.building.updatedAt"
                flex
            />

            <h6 class="mt-3 mb-0 font-weight-bold">{{ $t('common:address') }}</h6>
            <p class="mb-0">{{ mergePreview.building.address_street }}</p>
            <p class="mb-0">{{ mergePreview.building.address_zip }} {{ mergePreview.building.address_city }}</p>
            <p>{{ mergePreview.building.provinceName }} - {{ mergePreview.building.countryName }}</p>

            <h6 class="mt-3 mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.titles.billing-address') }}</h6>
            <p class="mb-0" v-show="mergePreview.building.billingPersonName"><span class="font-weight-bold">{{ $t('common:name') }}: </span>{{ mergePreview.building.billingPersonName }}</p>
            <div v-show="mergePreview.building.billing_street || mergePreview.building.billing_zip || mergePreview.building.billing_city">
              <span class="font-weight-bold">{{ $t('common:address') }}: </span>
              <p class="mb-0">{{ mergePreview.building.billing_street }}</p>
              <p class="mb-0">{{ mergePreview.building.billing_zip }} {{ mergePreview.building.billing_city }}</p>
            </div>
          </v-col>

          <!-- Right side -->
          <v-col>
            <OpenStreetMap
                v-show="mergePreview.building.latitude != null && mergePreview.building.longitude != null"
                ref="map"
                height="250"
                :lat="mergePreview.building.latitude"
                :long="mergePreview.building.longitude"
            />
          </v-col>
        </v-row>

        <div class="my-5"></div>

        <h6 class="mb-0 font-weight-bold">{{ $t('common:facility_plural') }}</h6>
        <DataTable
            :headers="facilityTable"
            :data="mergePreview.facilities"
        />

        <h6 class="mb-0 font-weight-bold">{{ $t('common:role_plural') }}</h6>
        <DataTable
            :headers="assignmentTable"
            :data="mergePreview.assignments"
        />

        <BaseButton
            :btn-text="$t('settings/settings:duplicate.preview.confirm-merge')"
            color="warning"
            @clickedButton="$refs.warningDialog.openDialog()"
        />
      </v-card-text>
    </v-card>

    <MergeWarningDialog
        ref="warningDialog"
        :objects-to-remove-names="secondaryBuildingNames"
        :object-translation="$t('common:building_plural')"
        @confirmMerge="confirmMerge"
    />
  </v-dialog>
</template>

<script>
import DuplicateCheckService from "@/modules/core/services/duplicate-check-service";

import LabelField from "@/modules/core/components/LabelField";
import OpenStreetMap from "@/modules/core/components/OpenStreetMap/OpenStreetMap";
import CountryService from "@/modules/core/services/country-service";
import Utils from "@/modules/core/services/utils";
import DataTable from "@/modules/core/components/DataTable/DataTable";
import BaseButton from "@/modules/core/components/Base/BaseButton";
import MergeWarningDialog from "@/modules/settings/views/settings/duplicateMerge/MergeWarningDialog";

export default {
  name: "BuildingMerging",
  components: {MergeWarningDialog, BaseButton, DataTable, OpenStreetMap, LabelField},
  data() {
    return {
      mergePreview: {},
      mainBuildingCountry: null,
      mainBuildingProvince: null,
      loaded: false,
      mainBuildingId: null,
      secondaryBuildingIds: [],
      secondaryBuildingNames: [],
      showDialog: false
    }
  },
  computed: {
    facilityTable() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:facility-number'), value: 'objectNumber'},
        {text: this.$t('settings/settings:duplicate.common.stairway'), value: 'stairway'},
        {text: this.$t('common:type'), value: 'typeName'},
        {text: this.$t('common:manager'), value: 'managerName'}
      ]
    },

    assignmentTable() {
      return [
        {text: this.$t('common:role'), value: 'roleName'},
        {text: this.$t('common:rank'), value: 'rank'},
        {text: this.$t('common:user'), value: 'userName'},
        {text: this.$t('common:email'), value: 'userEmail'}
      ]
    },

    currentLang() {
      return this.$store.state.translation.language;
    },
  },
  methods: {
    closeDialog() {
      this.mergePreview = {};
      this.loaded = false;
      this.mainBuildingId = null;
      this.secondaryBuildingIds = [];
      this.secondaryBuildingNames = [];
      this.showDialog = false;
    },

    createPreview(duplicateGroup) {
      this.showDialog = true;
      let buildingIds = [];
      for (let building of duplicateGroup.objects) {
        if (duplicateGroup.primaryObjectId !== building.id) {
          buildingIds.push(building.id);
          this.secondaryBuildingIds.push(building.id);
          this.secondaryBuildingNames.push(building.name)
        }
      }

      this.mainBuildingId = duplicateGroup.primaryObjectId;

      DuplicateCheckService.readBuildingMergePreview(duplicateGroup.primaryObjectId, buildingIds,
          (res) => {
            this.mergePreview = res.data;

            CountryService.readFromAPI(this.mergePreview.building.country,
                (res) => {
                  this.mainBuildingCountry = res.data;
                  this.mergePreview.building.countryName = Utils.translateCountry(this.mainBuildingCountry.name);

                  CountryService.readProvincesOfCountry(this.mergePreview.building.country,
                      (resProvinces) => {
                        resProvinces.data.forEach((province) => {
                          if (this.mergePreview.building.province === province.id) {
                            this.mainBuildingProvince = province;
                            this.mergePreview.building.provinceName = Utils.translateProvinces(this.mainBuildingCountry.name, province.name);
                          }
                        })

                        // recenter map if building has lat and long set
                        if (this.$refs.map && this.mergePreview.building.latitude && this.mergePreview.building.longitude) {
                          this.$refs.map.refreshMap();
                          this.$refs.map.recenterMap(this.mergePreview.building.latitude, this.mergePreview.building.longitude)
                        }

                        this.loaded = true;
                      },
                      (error) => {
                        this.$logger.error("Could not read provinces of country with id '" + this.mergePreview.building.country + "', because: " + error);
                      }
                  )
                },
                (error) => {
                  this.$logger.error("Could not read country data with id '" + this.mergePreview.building.country + "', because: " + error);
                }
            )
          },
          (error) => {
            this.$logger.error("Failed reading building merge preview, because: " + error);
            Utils.showFailureSnackbar(this.$t('settings/settings:duplicate.preview.snackbar.failed-reading-preview'))
          }
      )
    },

    confirmMerge() {
      this.$refs.warningDialog.closeDialog();
      DuplicateCheckService.mergeBuildings(this.mainBuildingId, this.secondaryBuildingIds,
          () => {
            Utils.showSuccessSnackbar(this.$t('settings/settings:duplicate.preview.snackbar.merge-successful', { name: this.$t('common:building_plural')}));
            this.$emit('finishedMerge', this.secondaryBuildingIds);
            this.closeDialog();
          },
          (error) => {
            Utils.showFailureSnackbar(this.$t('settings/settings:duplicate.preview.snackbar.merge-failed', { name: this.$t('common:building_plural')}));
            this.$logger.error("Failed merging buildings because: " + error);
          }
      )
    }
  }
}
</script>